/**
 * Exportação do relatório de vendas.
 *
 * `exceljs` e `jspdf` entram por import dinâmico, seguindo o que o livro caixa
 * e a exportação de passeios já fazem — nenhum dos dois pesa o bundle inicial.
 */

interface Periodo {
  dataInicio: string;
  dataFim: string;
}

export interface RelatorioVendas {
  resumo: {
    total: number;
    quantidade: number;
    ticketMedio: number;
    estornadas: number;
    totalEstornado: number;
  };
  porDia: { dia: string; total: number; quantidade: number }[];
  porFormaPagamento: { formaPagamento: string; total: number; quantidade: number }[];
  porCategoria: { categoria: string; total: number; unidades: number }[];
  rankingProdutos: {
    codigo: string;
    nome: string;
    categoria: string | null;
    total: number;
    unidades: number;
  }[];
}

const ROTULO_PAGAMENTO: Record<string, string> = {
  DINHEIRO: 'Dinheiro',
  PIX: 'PIX',
  CARTAO_CREDITO: 'Cartão de crédito',
  CARTAO_DEBITO: 'Cartão de débito',
};

const dataBr = (iso: string) => iso.split('-').reverse().join('/');

const nomeArquivo = (periodo: Periodo, extensao: string) =>
  `vendas-${periodo.dataInicio}-a-${periodo.dataFim}.${extensao}`;

export async function exportarVendasExcel(
  relatorio: RelatorioVendas,
  periodo: Periodo
): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();

  const resumo = workbook.addWorksheet('Resumo');
  resumo.columns = [
    { header: 'Indicador', key: 'indicador', width: 28 },
    { header: 'Valor', key: 'valor', width: 18 },
  ];
  resumo.addRows([
    { indicador: 'Período', valor: `${dataBr(periodo.dataInicio)} a ${dataBr(periodo.dataFim)}` },
    { indicador: 'Faturamento', valor: relatorio.resumo.total },
    { indicador: 'Vendas', valor: relatorio.resumo.quantidade },
    { indicador: 'Ticket médio', valor: relatorio.resumo.ticketMedio },
    { indicador: 'Vendas estornadas', valor: relatorio.resumo.estornadas },
    { indicador: 'Valor estornado', valor: relatorio.resumo.totalEstornado },
  ]);

  const dias = workbook.addWorksheet('Por dia');
  dias.columns = [
    { header: 'Dia', key: 'dia', width: 14 },
    { header: 'Faturamento', key: 'total', width: 16 },
    { header: 'Vendas', key: 'quantidade', width: 12 },
  ];
  dias.addRows(relatorio.porDia.map((d) => ({ ...d, dia: dataBr(d.dia) })));

  const pagamentos = workbook.addWorksheet('Pagamento');
  pagamentos.columns = [
    { header: 'Forma', key: 'forma', width: 22 },
    { header: 'Faturamento', key: 'total', width: 16 },
    { header: 'Vendas', key: 'quantidade', width: 12 },
  ];
  pagamentos.addRows(
    relatorio.porFormaPagamento.map((f) => ({
      forma: ROTULO_PAGAMENTO[f.formaPagamento] ?? f.formaPagamento,
      total: f.total,
      quantidade: f.quantidade,
    }))
  );

  const categorias = workbook.addWorksheet('Categoria');
  categorias.columns = [
    { header: 'Categoria', key: 'categoria', width: 26 },
    { header: 'Faturamento', key: 'total', width: 16 },
    { header: 'Unidades', key: 'unidades', width: 12 },
  ];
  categorias.addRows(relatorio.porCategoria);

  const produtos = workbook.addWorksheet('Produtos');
  produtos.columns = [
    { header: 'Código', key: 'codigo', width: 12 },
    { header: 'Produto', key: 'nome', width: 34 },
    { header: 'Categoria', key: 'categoria', width: 20 },
    { header: 'Faturamento', key: 'total', width: 16 },
    { header: 'Unidades', key: 'unidades', width: 12 },
  ];
  produtos.addRows(
    relatorio.rankingProdutos.map((p) => ({ ...p, categoria: p.categoria ?? 'Sem categoria' }))
  );

  for (const aba of [resumo, dias, pagamentos, categorias, produtos]) {
    aba.getRow(1).font = { bold: true };
    aba.getColumn('total' in aba.columns ? 2 : 2).numFmt = '#,##0.00';
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer]));
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeArquivo(periodo, 'xlsx');
  link.click();
  URL.revokeObjectURL(url);
}

export async function exportarVendasPdf(
  relatorio: RelatorioVendas,
  periodo: Periodo
): Promise<void> {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const doc = new jsPDF();
  const moeda = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  doc.setFontSize(16);
  doc.text('Relatório de Vendas', 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(`${dataBr(periodo.dataInicio)} a ${dataBr(periodo.dataFim)}`, 14, 24);
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 32,
    head: [['Faturamento', 'Vendas', 'Ticket médio', 'Estornadas']],
    body: [
      [
        moeda(relatorio.resumo.total),
        String(relatorio.resumo.quantidade),
        moeda(relatorio.resumo.ticketMedio),
        `${relatorio.resumo.estornadas} (${moeda(relatorio.resumo.totalEstornado)})`,
      ],
    ],
    styles: { fontSize: 10 },
    headStyles: { fillColor: [2, 132, 199] },
    margin: { left: 14, right: 14 },
  });

  const proximaLinha = () =>
    (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;

  const secao = (titulo: string, head: string[], body: string[][]) => {
    doc.setFontSize(12);
    doc.text(titulo, 14, proximaLinha());
    autoTable(doc, {
      startY: proximaLinha() + 3,
      head: [head],
      body,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [2, 132, 199] },
      margin: { left: 14, right: 14 },
    });
  };

  secao(
    'Por forma de pagamento',
    ['Forma', 'Faturamento', 'Vendas'],
    relatorio.porFormaPagamento.map((f) => [
      ROTULO_PAGAMENTO[f.formaPagamento] ?? f.formaPagamento,
      moeda(f.total),
      String(f.quantidade),
    ])
  );

  secao(
    'Por categoria',
    ['Categoria', 'Faturamento', 'Unidades'],
    relatorio.porCategoria.map((c) => [c.categoria, moeda(c.total), String(c.unidades)])
  );

  secao(
    'Produtos mais vendidos',
    ['#', 'Código', 'Produto', 'Faturamento', 'Un'],
    relatorio.rankingProdutos.map((p, i) => [
      String(i + 1),
      p.codigo,
      p.nome,
      moeda(p.total),
      String(p.unidades),
    ])
  );

  doc.save(nomeArquivo(periodo, 'pdf'));
}
